import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtAUD } from '@/lib/format';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

export default function OrderSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const orderId = params.get('order_id');
    const [state, setState] = useState({ status: 'polling', payment_status: null, amount: 0 });
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: 'error', payment_status: null, amount: 0 });
            return;
        }
        let stopped = false;
        const poll = async () => {
            if (stopped) return;
            attempts.current += 1;
            try {
                const r = await api.get(`/payments/checkout/status/${sessionId}`);
                if (r.data.payment_status === 'paid') {
                    setState({ status: 'paid', payment_status: 'paid', amount: r.data.amount_total / 100 });
                    return;
                }
                if (r.data.status === 'expired') {
                    setState({ status: 'expired', payment_status: 'expired', amount: 0 });
                    return;
                }
                if (attempts.current >= 6) {
                    setState({ status: 'timeout', payment_status: r.data.payment_status, amount: r.data.amount_total / 100 });
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                if (attempts.current >= 3) {
                    setState({ status: 'error', payment_status: null, amount: 0 });
                    return;
                }
                setTimeout(poll, 2000);
            }
        };
        poll();
        return () => { stopped = true; };
    }, [sessionId]);

    return (
        <div className="py-16 max-w-2xl mx-auto" data-testid="order-success-page">
            {state.status === 'polling' && (
                <div className="text-center">
                    <Loader2 size={48} className="animate-spin mx-auto mb-6 text-crimson" />
                    <h1 className="font-display text-3xl uppercase tracking-tighter mb-2">Confirming payment…</h1>
                    <p className="text-zinc-400 font-mono-data text-sm">Hang tight, this only takes a moment.</p>
                </div>
            )}
            {state.status === 'paid' && (
                <div className="text-center" data-testid="success-paid">
                    <CheckCircle2 size={56} className="mx-auto mb-6 text-emerald-400" />
                    <div className="font-mono-data text-xs uppercase text-emerald-400 tracking-widest mb-2">Payment Confirmed</div>
                    <h1 className="font-display text-5xl uppercase tracking-tighter mb-3">Order Placed.</h1>
                    <p className="text-zinc-300 mb-2">Locals HQ has received your order.</p>
                    <p className="font-mono-data text-sm text-zinc-400 mb-8">
                        Charged {fmtAUD(state.amount)} · Order ID {orderId?.slice(0, 8)}
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Link to="/orders" className="bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider px-5 py-3">
                            View my orders
                        </Link>
                        <Link to="/" className="border border-zinc-700 hover:border-crimson font-display uppercase tracking-wider px-5 py-3">
                            Back to catalog
                        </Link>
                    </div>
                </div>
            )}
            {(state.status === 'expired' || state.status === 'error') && (
                <div className="text-center">
                    <XCircle size={56} className="mx-auto mb-6 text-crimson" />
                    <h1 className="font-display text-3xl uppercase tracking-tighter mb-3">Payment not completed</h1>
                    <p className="text-zinc-400 mb-8">Your order is saved but unpaid. You can retry payment from your orders.</p>
                    <Link to="/orders" className="bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider px-5 py-3">
                        View my orders
                    </Link>
                </div>
            )}
            {state.status === 'timeout' && (
                <div className="text-center">
                    <Loader2 size={48} className="mx-auto mb-6 text-zinc-400" />
                    <h1 className="font-display text-3xl uppercase tracking-tighter mb-3">Still processing…</h1>
                    <p className="text-zinc-400 mb-8">Check your orders page in a minute.</p>
                    <Link to="/orders" className="bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider px-5 py-3">
                        View my orders
                    </Link>
                </div>
            )}
        </div>
    );
}
